// Base de données des interactions médicamenteuses avec les plantes et remèdes naturels

export interface MedicationType {
  id: string;
  names: string[]; // Noms et synonymes
  category: string;
  subcategory?: string;
  description: string;
}

export interface PlantMedicationInteraction {
  plant: string;
  medicationTypes: string[]; // IDs des types de médicaments
  severity: 'minor' | 'moderate' | 'major';
  description: string;
  mechanism?: string;
}

export interface AllergyWarning {
  allergen: string;
  synonyms: string[];
  plantsToAvoid: string[];
  category: string;
  description: string;
}

// =====================================================
// TYPES DE MÉDICAMENTS - LISTE EXHAUSTIVE
// =====================================================

export const medicationTypes: MedicationType[] = [
  // 1. MÉDICAMENTS CONTRE LA DOULEUR ET LA FIÈVRE
  {
    id: 'analgesic',
    names: ['antalgique', 'antalgiques', 'analgésique', 'analgésiques', 'douleur', 'anti-douleur'],
    category: 'Douleur et fièvre',
    subcategory: 'Analgésiques',
    description: 'Médicaments qui soulagent la douleur'
  },
  {
    id: 'antipyretic',
    names: ['antipyrétique', 'antipyrétiques', 'fièvre', 'anti-fièvre'],
    category: 'Douleur et fièvre',
    subcategory: 'Antipyrétiques',
    description: 'Médicaments qui font baisser la fièvre'
  },
  {
    id: 'antiinflammatory',
    names: ['anti-inflammatoire', 'anti-inflammatoires', 'antiinflammatoire', 'ains', 'ibuprofène', 'nurofen', 'advil', 'ketoprofène', 'profénid', 'diclofénac', 'voltarène', 'naproxène', 'apranax', 'célécoxib', 'celebrex', 'piroxicam', 'feldène', 'indométacine'],
    category: 'Douleur et fièvre',
    subcategory: 'Anti-inflammatoires non stéroïdiens (AINS)',
    description: 'Médicaments contre l\'inflammation et la douleur'
  },
  {
    id: 'paracetamol',
    names: ['paracétamol', 'doliprane', 'dafalgan', 'efferalgan', 'tylenol', 'acétaminophène'],
    category: 'Douleur et fièvre',
    subcategory: 'Antalgiques',
    description: 'Médicament contre la douleur et la fièvre'
  },
  {
    id: 'opioid',
    names: ['opioïde', 'opioïdes', 'opiacé', 'opiacés', 'morphine', 'tramadol', 'codéine', 'oxycodone', 'fentanyl', 'buprénorphine', 'subutex', 'méthadone', 'lamaline', 'ixprim', 'topalgic'],
    category: 'Douleur et fièvre',
    subcategory: 'Opioïdes',
    description: 'Analgésiques puissants pour douleurs modérées à sévères'
  },

  // 2. MÉDICAMENTS ANTI-INFECTIEUX
  {
    id: 'antibiotic',
    names: ['antibiotique', 'antibiotiques', 'amoxicilline', 'augmentin', 'clamoxyl', 'azithromycine', 'zithromax', 'ciprofloxacine', 'ciflox', 'doxycycline', 'pénicilline', 'céphalosporine', 'macrolide', 'fluoroquinolone', 'métronidazole', 'flagyl'],
    category: 'Anti-infectieux',
    subcategory: 'Antibiotiques',
    description: 'Médicaments contre les infections bactériennes'
  },
  {
    id: 'antiviral',
    names: ['antiviral', 'antiviraux', 'aciclovir', 'zovirax', 'valaciclovir', 'zelitrex', 'oseltamivir', 'tamiflu', 'ribavirine'],
    category: 'Anti-infectieux',
    subcategory: 'Antiviraux',
    description: 'Médicaments contre les infections virales'
  },
  {
    id: 'antifungal',
    names: ['antifongique', 'antifongiques', 'antimycotique', 'antimycotiques', 'fluconazole', 'triflucan', 'itraconazole', 'sporanox', 'terbinafine', 'lamisil', 'nystatine'],
    category: 'Anti-infectieux',
    subcategory: 'Antifongiques',
    description: 'Médicaments contre les champignons'
  },
  {
    id: 'antiparasitic',
    names: ['antiparasitaire', 'antiparasitaires', 'vermifuge', 'ivermectine', 'stromectol', 'albendazole', 'fluvermal', 'métronidazole'],
    category: 'Anti-infectieux',
    subcategory: 'Antiparasitaires',
    description: 'Médicaments contre les parasites'
  },
  {
    id: 'antituberculous',
    names: ['antituberculeux', 'tuberculose', 'isoniazide', 'rifampicine', 'rifadine', 'pyrazinamide', 'éthambutol'],
    category: 'Anti-infectieux',
    subcategory: 'Antituberculeux',
    description: 'Médicaments contre la tuberculose'
  },

  // 3. MÉDICAMENTS DU SYSTÈME CARDIOVASCULAIRE
  {
    id: 'antihypertensive',
    names: ['antihypertenseur', 'antihypertenseurs', 'tenseur', 'hypertendu', 'tension', 'hypertension'],
    category: 'Cardiovasculaire',
    subcategory: 'Antihypertenseurs',
    description: 'Médicaments contre l\'hypertension artérielle'
  },
  {
    id: 'diuretic',
    names: ['diurétique', 'diurétiques', 'lasilix', 'furosémide', 'aldactone', 'spironolactone', 'hydrochlorothiazide', 'esidrex', 'indapamide', 'fludex'],
    category: 'Cardiovasculaire',
    subcategory: 'Diurétiques',
    description: 'Médicaments qui augmentent la diurèse'
  },
  {
    id: 'beta_blocker',
    names: ['bêta-bloquant', 'bêtabloquant', 'beta-bloquant', 'betabloquant', 'bêta bloquant', 'bisoprolol', 'atenolol', 'nébivolol', 'métoprolol', 'propranolol', 'avlocardyl', 'cardensiel'],
    category: 'Cardiovasculaire',
    subcategory: 'Bêta-bloquants',
    description: 'Médicaments qui ralentissent le cœur'
  },
  {
    id: 'ace_inhibitor',
    names: ['iec', 'inhibiteur enzyme conversion', 'ramipril', 'triatec', 'périndopril', 'coversyl', 'énalapril', 'renitec', 'lisinopril', 'zestril', 'captopril'],
    category: 'Cardiovasculaire',
    subcategory: 'IEC',
    description: 'Inhibiteurs de l\'enzyme de conversion'
  },
  {
    id: 'anticoagulant',
    names: ['anticoagulant', 'anticoagulants', 'coumadine', 'warfarine', 'sintrom', 'préviscan', 'xarelto', 'rivaroxaban', 'eliquis', 'apixaban', 'pradaxa', 'dabigatran', 'lovenox', 'héparine', 'fluidifiant sanguin', 'fluidifiants'],
    category: 'Cardiovasculaire',
    subcategory: 'Anticoagulants',
    description: 'Médicaments qui empêchent la coagulation du sang'
  },
  {
    id: 'antiaggregant',
    names: ['antiagrégant', 'antiagrégants', 'aspirine', 'kardégic', 'aspégic', 'plavix', 'clopidogrel', 'efient', 'prasugrel', 'brilique', 'ticagrélor'],
    category: 'Cardiovasculaire',
    subcategory: 'Antiagrégants plaquettaires',
    description: 'Médicaments qui empêchent l\'agrégation des plaquettes'
  },
  {
    id: 'antiarrhythmic',
    names: ['antiarythmique', 'antiarythmiques', 'amiodarone', 'cordarone', 'flécaïnide', 'flécaine', 'propafénone', 'rythmol', 'sotalol'],
    category: 'Cardiovasculaire',
    subcategory: 'Antiarythmiques',
    description: 'Médicaments contre les troubles du rythme cardiaque'
  },
  {
    id: 'statin',
    names: ['statine', 'statines', 'hypolipémiant', 'hypolipémiants', 'cholestérol', 'anticholestérol', 'atorvastatine', 'tahor', 'simvastatine', 'zocor', 'rosuvastatine', 'crestor', 'pravastatine', 'elisor', 'ézétimibe', 'ezetrol', 'fénofibrate', 'lipanthyl'],
    category: 'Cardiovasculaire',
    subcategory: 'Hypolipémiants (Statines)',
    description: 'Médicaments contre le cholestérol'
  },
  {
    id: 'cardiac_glycoside',
    names: ['digitalique', 'digitaliques', 'digoxine', 'digitalis', 'digitaline', 'hémigoxine'],
    category: 'Cardiovasculaire',
    subcategory: 'Digitaliques',
    description: 'Médicaments pour le cœur (insuffisance cardiaque)'
  },
  {
    id: 'calcium_channel_blocker',
    names: ['inhibiteur calcique', 'antagoniste calcium', 'amlodipine', 'amlor', 'nifédipine', 'adalate', 'diltiazem', 'tildiem', 'vérapamil', 'isoptine'],
    category: 'Cardiovasculaire',
    subcategory: 'Inhibiteurs calciques',
    description: 'Médicaments vasodilatateurs'
  },
  {
    id: 'angiotensin_receptor_blocker',
    names: ['ara2', 'sartan', 'losartan', 'cozaar', 'valsartan', 'tareg', 'irbésartan', 'aprovel', 'candésartan', 'atacand', 'olmésartan'],
    category: 'Cardiovasculaire',
    subcategory: 'ARA2 (Sartans)',
    description: 'Antagonistes des récepteurs de l\'angiotensine II'
  },

  // 4. MÉDICAMENTS DU SYSTÈME RESPIRATOIRE
  {
    id: 'bronchodilator',
    names: ['bronchodilatateur', 'bronchodilatateurs', 'ventoline', 'salbutamol', 'terbutaline', 'bricanyl', 'formotérol', 'salmétérol', 'sérévent', 'atrovent', 'ipratropium', 'spiriva', 'tiotropium'],
    category: 'Respiratoire',
    subcategory: 'Bronchodilatateurs',
    description: 'Médicaments qui dilatent les bronches'
  },
  {
    id: 'inhaled_corticosteroid',
    names: ['corticoïde inhalé', 'corticoïdes inhalés', 'béclométasone', 'bécotide', 'budésonide', 'pulmicort', 'fluticasone', 'flixotide', 'symbicort', 'seretide'],
    category: 'Respiratoire',
    subcategory: 'Corticoïdes inhalés',
    description: 'Anti-inflammatoires pour les voies respiratoires'
  },
  {
    id: 'antitussive',
    names: ['antitussif', 'antitussifs', 'toux', 'codéine toux', 'dextrométhorphane', 'toplexil', 'clarix', 'humex toux', 'pholcodine'],
    category: 'Respiratoire',
    subcategory: 'Antitussifs',
    description: 'Médicaments contre la toux'
  },
  {
    id: 'expectorant',
    names: ['expectorant', 'expectorants', 'mucolytique', 'mucolytiques', 'acétylcystéine', 'mucomyst', 'fluimucil', 'carbocistéine', 'bronchokod', 'ambroxol', 'muxol'],
    category: 'Respiratoire',
    subcategory: 'Expectorants/Mucolytiques',
    description: 'Médicaments qui fluidifient les sécrétions'
  },
  {
    id: 'antiasthmatic',
    names: ['antiasthmatique', 'antiasthmatiques', 'asthme', 'montélukast', 'singulair', 'théophylline'],
    category: 'Respiratoire',
    subcategory: 'Antiasthmatiques',
    description: 'Médicaments pour le traitement de l\'asthme'
  },

  // 5. MÉDICAMENTS DU SYSTÈME DIGESTIF
  {
    id: 'antacid',
    names: ['antiacide', 'antiacides', 'maalox', 'gaviscon', 'rennie', 'phosphalugel', 'hydroxyde aluminium', 'hydroxyde magnésium'],
    category: 'Digestif',
    subcategory: 'Antiacides',
    description: 'Médicaments qui neutralisent l\'acidité gastrique'
  },
  {
    id: 'ppi',
    names: ['ipp', 'inhibiteur pompe protons', 'oméprazole', 'mopral', 'inexium', 'esoméprazole', 'pantoprazole', 'eupantol', 'lansoprazole', 'lanzor', 'rabéprazole', 'pariet'],
    category: 'Digestif',
    subcategory: 'Inhibiteurs de la pompe à protons (IPP)',
    description: 'Médicaments contre l\'acidité gastrique'
  },
  {
    id: 'h2_blocker',
    names: ['anti-h2', 'antihistaminique h2', 'ranitidine', 'azantac', 'famotidine', 'pepcid', 'cimétidine', 'tagamet'],
    category: 'Digestif',
    subcategory: 'Anti-H2',
    description: 'Antihistaminiques H2 pour l\'estomac'
  },
  {
    id: 'antidiarrheal',
    names: ['antidiarrhéique', 'antidiarrhéiques', 'lopéramide', 'imodium', 'tiorfan', 'racécadotril', 'smecta', 'diosmectite'],
    category: 'Digestif',
    subcategory: 'Antidiarrhéiques',
    description: 'Médicaments contre la diarrhée'
  },
  {
    id: 'laxative',
    names: ['laxatif', 'laxatifs', 'constipation', 'macrogol', 'forlax', 'movicol', 'duphalac', 'lactulose', 'microlax', 'dulcolax', 'bisacodyl', 'lansoyl', 'psyllium', 'normacol'],
    category: 'Digestif',
    subcategory: 'Laxatifs',
    description: 'Médicaments contre la constipation'
  },
  {
    id: 'antispasmodic',
    names: ['antispasmodique', 'antispasmodiques', 'spasfon', 'phloroglucinol', 'debridat', 'trimébutine', 'duspatalin', 'mébévérine', 'buscopan', 'scopolamine'],
    category: 'Digestif',
    subcategory: 'Antispasmodiques',
    description: 'Médicaments contre les spasmes digestifs'
  },
  {
    id: 'antiemetic',
    names: ['antiémétique', 'antiémétiques', 'nausée', 'vomissement', 'métoclopramide', 'primpéran', 'dompéridone', 'motilium', 'ondansétron', 'zophren', 'vogalène', 'métopimazine'],
    category: 'Digestif',
    subcategory: 'Antiémétiques',
    description: 'Médicaments contre les nausées et vomissements'
  },

  // 6. MÉDICAMENTS HORMONAUX ET MÉTABOLIQUES
  {
    id: 'thyroid_hormone',
    names: ['hormone thyroïdienne', 'hormones thyroïdiennes', 'levothyrox', 'lévothyroxine', 'euthyrox', 'l-thyroxine', 'tcaps', 'thyrofix'],
    category: 'Hormonal et métabolique',
    subcategory: 'Hormones thyroïdiennes',
    description: 'Hormones pour l\'hypothyroïdie'
  },
  {
    id: 'antithyroid',
    names: ['antithyroïdien', 'antithyroïdiens', 'néomercazole', 'carbimazole', 'thyrozol', 'propylthiouracile'],
    category: 'Hormonal et métabolique',
    subcategory: 'Antithyroïdiens',
    description: 'Médicaments contre l\'hyperthyroïdie'
  },
  {
    id: 'insulin',
    names: ['insuline', 'insulines', 'novorapid', 'humalog', 'lantus', 'levemir', 'toujeo', 'tresiba', 'insulatard'],
    category: 'Hormonal et métabolique',
    subcategory: 'Insulines',
    description: 'Insulines pour le diabète'
  },
  {
    id: 'oral_antidiabetic',
    names: ['antidiabétique oral', 'antidiabétiques oraux', 'diabète', 'metformine', 'glucophage', 'stagid', 'sulfamide', 'daonil', 'glibenclamide', 'gliclazide', 'diamicron', 'januvia', 'sitagliptine', 'galvus', 'vildagliptine', 'jardiance', 'empagliflozine', 'forxiga', 'dapagliflozine'],
    category: 'Hormonal et métabolique',
    subcategory: 'Antidiabétiques oraux',
    description: 'Médicaments oraux contre le diabète'
  },
  {
    id: 'glp1_agonist',
    names: ['glp-1', 'analogue glp1', 'trulicity', 'dulaglutide', 'victoza', 'liraglutide', 'ozempic', 'sémaglutide', 'byetta', 'exénatide', 'wegovy', 'saxenda'],
    category: 'Hormonal et métabolique',
    subcategory: 'Agonistes GLP-1',
    description: 'Analogues du GLP-1 pour diabète/obésité'
  },
  {
    id: 'systemic_corticosteroid',
    names: ['corticoïde', 'corticoïdes', 'corticostéroïde', 'corticostéroïdes', 'cortisone', 'prednisone', 'cortancyl', 'prednisolone', 'solupred', 'dexaméthasone', 'méthylprednisolone', 'médrol', 'bétaméthasone', 'célestène'],
    category: 'Hormonal et métabolique',
    subcategory: 'Corticostéroïdes systémiques',
    description: 'Anti-inflammatoires stéroïdiens par voie générale'
  },
  {
    id: 'contraceptive',
    names: ['contraceptif', 'contraceptifs', 'pilule', 'pilule contraceptive', 'contraception', 'stérilet hormonal', 'patch contraceptif', 'implant contraceptif', 'nuvaring', 'anneau', 'minidril', 'leeloo', 'optilova', 'cerazette', 'microval', 'mirena', 'nexplanon'],
    category: 'Hormonal et métabolique',
    subcategory: 'Contraceptifs hormonaux',
    description: 'Méthodes de contraception hormonales'
  },
  {
    id: 'menopause_treatment',
    names: ['ménopause', 'traitement ménopause', 'ths', 'traitement hormonal substitutif', 'oestrogène', 'estrogène', 'progestérone', 'oestradiol', 'provames', 'climara', 'estreva', 'utrogestan', 'livial', 'tibolone'],
    category: 'Hormonal et métabolique',
    subcategory: 'Traitements de la ménopause',
    description: 'Traitements hormonaux de la ménopause'
  },

  // 7. MÉDICAMENTS DU SYSTÈME NERVEUX
  {
    id: 'anxiolytic',
    names: ['anxiolytique', 'anxiolytiques', 'benzodiazépine', 'benzodiazépines', 'lexomil', 'xanax', 'alprazolam', 'bromazépam', 'diazépam', 'valium', 'lorazépam', 'témesta', 'calmant', 'calmants', 'lysanxia', 'prazépam', 'tranxène', 'clorazépate', 'séresta', 'oxazépam'],
    category: 'Système nerveux',
    subcategory: 'Anxiolytiques',
    description: 'Médicaments contre l\'anxiété'
  },
  {
    id: 'antidepressant',
    names: ['antidépresseur', 'antidépresseurs', 'prozac', 'fluoxétine', 'sertraline', 'zoloft', 'paroxétine', 'deroxat', 'escitalopram', 'seroplex', 'citalopram', 'seropram', 'venlafaxine', 'effexor', 'duloxétine', 'cymbalta', 'mirtazapine', 'norset', 'amitriptyline', 'laroxyl', 'clomipramine', 'anafranil', 'irs', 'isrs', 'irsna', 'tricyclique'],
    category: 'Système nerveux',
    subcategory: 'Antidépresseurs',
    description: 'Médicaments contre la dépression'
  },
  {
    id: 'antipsychotic',
    names: ['antipsychotique', 'antipsychotiques', 'neuroleptique', 'neuroleptiques', 'rispéridone', 'risperdal', 'olanzapine', 'zyprexa', 'aripiprazole', 'abilify', 'quétiapine', 'xeroquel', 'halopéridol', 'haldol', 'chlorpromazine', 'largactil', 'loxapine', 'loxapac', 'clozapine', 'leponex'],
    category: 'Système nerveux',
    subcategory: 'Antipsychotiques (Neuroleptiques)',
    description: 'Médicaments pour les troubles psychiatriques'
  },
  {
    id: 'hypnotic',
    names: ['hypnotique', 'hypnotiques', 'somnifère', 'somnifères', 'stilnox', 'zolpidem', 'imovane', 'zopiclone', 'havlane', 'loprazolam', 'noctamide', 'lormetazépam', 'mogadon', 'nitrazépam', 'rohypnol', 'flunitrazépam'],
    category: 'Système nerveux',
    subcategory: 'Hypnotiques/Somnifères',
    description: 'Médicaments pour le sommeil'
  },
  {
    id: 'antiepileptic',
    names: ['antiépileptique', 'antiépileptiques', 'anticonvulsivant', 'épilepsie', 'dépakine', 'valproate', 'lamictal', 'lamotrigine', 'keppra', 'lévétiracétam', 'tégrétol', 'carbamazépine', 'trileptal', 'oxcarbazépine', 'neurontin', 'gabapentine', 'lyrica', 'prégabaline', 'dilantin', 'phénytoïne', 'gardénal', 'phénobarbital', 'topamax', 'topiramate'],
    category: 'Système nerveux',
    subcategory: 'Antiépileptiques',
    description: 'Médicaments contre l\'épilepsie'
  },
  {
    id: 'psychostimulant',
    names: ['psychostimulant', 'psychostimulants', 'méthylphénidate', 'ritaline', 'concerta', 'quasym', 'medikinet', 'amphétamine', 'lisdexamfétamine', 'vyvanse', 'modafinil', 'modiodal'],
    category: 'Système nerveux',
    subcategory: 'Psychostimulants',
    description: 'Stimulants du système nerveux central'
  },
  {
    id: 'lithium',
    names: ['lithium', 'téralithe'],
    category: 'Système nerveux',
    subcategory: 'Thymorégulateurs',
    description: 'Stabilisateur de l\'humeur'
  },
  {
    id: 'anti_alzheimer',
    names: ['anti-alzheimer', 'alzheimer', 'aricept', 'donépézil', 'exelon', 'rivastigmine', 'reminyl', 'galantamine', 'ebixa', 'mémantine'],
    category: 'Système nerveux',
    subcategory: 'Anti-Alzheimer',
    description: 'Médicaments pour la maladie d\'Alzheimer'
  },
  {
    id: 'anti_parkinson',
    names: ['antiparkinsonien', 'parkinson', 'levodopa', 'modopar', 'sinemet', 'stalevo', 'ropinirole', 'requip', 'pramipexole', 'sifrol', 'rasagiline', 'azilect'],
    category: 'Système nerveux',
    subcategory: 'Antiparkinsoniens',
    description: 'Médicaments pour la maladie de Parkinson'
  },
  {
    id: 'antimigraine',
    names: ['antimigraine', 'migraine', 'triptan', 'sumatriptan', 'imigrane', 'zomig', 'zolmitriptan', 'relpax', 'élétriptan', 'maxalt', 'rizatriptan'],
    category: 'Système nerveux',
    subcategory: 'Antimigraineux',
    description: 'Médicaments contre la migraine'
  },

  // 8. MÉDICAMENTS ANTI-ALLERGIQUES ET IMMUNITAIRES
  {
    id: 'antihistamine',
    names: ['antihistaminique', 'antihistaminiques', 'allergie', 'cétirizine', 'zyrtec', 'virlix', 'loratadine', 'clarityne', 'desloratadine', 'aerius', 'lévocétirizine', 'xyzall', 'bilastine', 'bilaska', 'fexofénadine', 'telfast', 'polaramine', 'dexchlorphéniramine'],
    category: 'Anti-allergique et immunitaire',
    subcategory: 'Antihistaminiques',
    description: 'Médicaments contre les allergies'
  },
  {
    id: 'immunosuppressant',
    names: ['immunosuppresseur', 'immunosuppresseurs', 'ciclosporine', 'néoral', 'sandimmun', 'tacrolimus', 'prograf', 'advagraf', 'mycophénolate', 'cellcept', 'azathioprine', 'imurel', 'méthotrexate', 'novatrex', 'léflunomide', 'arava'],
    category: 'Anti-allergique et immunitaire',
    subcategory: 'Immunosuppresseurs',
    description: 'Médicaments qui suppriment l\'immunité'
  },
  {
    id: 'immunostimulant',
    names: ['immunostimulant', 'immunostimulants', 'stimulant immunitaire', 'interféron', 'imudon'],
    category: 'Anti-allergique et immunitaire',
    subcategory: 'Immunostimulants',
    description: 'Médicaments qui stimulent l\'immunité'
  },
  {
    id: 'biotherapy',
    names: ['biothérapie', 'biothérapies', 'anticorps monoclonal', 'anti-tnf', 'humira', 'adalimumab', 'enbrel', 'etanercept', 'remicade', 'infliximab', 'cosentyx', 'sécukinumab', 'stelara', 'ustekinumab', 'dupixent', 'dupilumab', 'xolair', 'omalizumab'],
    category: 'Anti-allergique et immunitaire',
    subcategory: 'Biothérapies',
    description: 'Thérapies biologiques ciblées'
  },

  // 9. MÉDICAMENTS ANTICANCÉREUX
  {
    id: 'chemotherapy',
    names: ['chimiothérapie', 'chimio', 'cytotoxique', 'cytotoxiques', 'méthotrexate', 'cyclophosphamide', 'endoxan', 'doxorubicine', 'adriamycine', 'cisplatine', 'carboplatine', 'paclitaxel', 'taxol', 'docétaxel', 'taxotère', '5-fu', 'fluorouracile', 'vincristine', 'étoposide'],
    category: 'Anticancéreux',
    subcategory: 'Chimiothérapies cytotoxiques',
    description: 'Médicaments de chimiothérapie'
  },
  {
    id: 'targeted_therapy',
    names: ['thérapie ciblée', 'thérapies ciblées', 'inhibiteur tyrosine kinase', 'imatinib', 'glivec', 'erlotinib', 'tarceva', 'gefitinib', 'iressa', 'sorafénib', 'nexavar', 'sunitinib', 'sutent', 'vémurafénib', 'zelboraf', 'ibrutinib', 'imbruvica'],
    category: 'Anticancéreux',
    subcategory: 'Thérapies ciblées',
    description: 'Traitements ciblés du cancer'
  },
  {
    id: 'cancer_immunotherapy',
    names: ['immunothérapie cancer', 'immunothérapie anticancéreuse', 'anti-pd1', 'anti-pdl1', 'pembrolizumab', 'keytruda', 'nivolumab', 'opdivo', 'atézolizumab', 'tecentriq', 'ipilimumab', 'yervoy'],
    category: 'Anticancéreux',
    subcategory: 'Immunothérapies anticancéreuses',
    description: 'Immunothérapies contre le cancer'
  },
  {
    id: 'cancer_hormone_therapy',
    names: ['hormonothérapie cancer', 'hormonothérapie anticancéreuse', 'tamoxifène', 'nolvadex', 'anastrozole', 'arimidex', 'létrozole', 'femara', 'exémestane', 'aromasine', 'bicalutamide', 'casodex', 'enzalutamide', 'xtandi', 'abiratérone', 'zytiga'],
    category: 'Anticancéreux',
    subcategory: 'Hormonothérapies anticancéreuses',
    description: 'Traitements hormonaux du cancer'
  },

  // 10. MÉDICAMENTS DERMATOLOGIQUES
  {
    id: 'skin_antiseptic',
    names: ['antiseptique cutané', 'antiseptiques', 'bétadine', 'povidone iodée', 'chlorhexidine', 'biseptine', 'hexomédine', 'dakin'],
    category: 'Dermatologique',
    subcategory: 'Antiseptiques',
    description: 'Antiseptiques pour la peau'
  },
  {
    id: 'topical_antibiotic',
    names: ['antibiotique local', 'antibiotique topique', 'fucidine', 'acide fusidique', 'mupiderm', 'mupirocine', 'érythromycine topique'],
    category: 'Dermatologique',
    subcategory: 'Antibiotiques locaux',
    description: 'Antibiotiques à usage cutané'
  },
  {
    id: 'topical_antifungal',
    names: ['antifongique local', 'antifongique topique', 'mycose crème', 'pévaryl', 'éconazole', 'fazol', 'isoconazole', 'lamisilate', 'terbinafine crème', 'ciclopirox', 'mycoster'],
    category: 'Dermatologique',
    subcategory: 'Antifongiques locaux',
    description: 'Antifongiques à usage cutané'
  },
  {
    id: 'topical_corticosteroid',
    names: ['corticoïde topique', 'corticoïde local', 'dermocorticoïde', 'dermocorticoïdes', 'diprosone', 'bétaméthasone crème', 'dermoval', 'clobétasol', 'locoid', 'hydrocortisone', 'nérisone', 'diflucortolone', 'tridesonit', 'désonide'],
    category: 'Dermatologique',
    subcategory: 'Corticoïdes topiques',
    description: 'Anti-inflammatoires locaux pour la peau'
  },
  {
    id: 'acne_treatment',
    names: ['acné', 'traitement acné', 'peroxyde benzoyle', 'cutacnyl', 'eclaran', 'adapalène', 'différine', 'trétinoïne', 'effederm', 'isotrétinoïne', 'roaccutane', 'curacné', 'procuta'],
    category: 'Dermatologique',
    subcategory: 'Traitements de l\'acné',
    description: 'Médicaments contre l\'acné'
  },
  {
    id: 'psoriasis_treatment',
    names: ['psoriasis', 'traitement psoriasis', 'daivobet', 'calcipotriol', 'daivonex', 'dovobet'],
    category: 'Dermatologique',
    subcategory: 'Traitements du psoriasis',
    description: 'Médicaments contre le psoriasis'
  },

  // 11. MÉDICAMENTS OPHTALMIQUES ET ORL
  {
    id: 'eye_drops',
    names: ['collyre', 'collyres', 'gouttes oculaires', 'larmes artificielles', 'hylo', 'optive', 'systane', 'refresh', 'lacryvisc'],
    category: 'Ophtalmique et ORL',
    subcategory: 'Collyres',
    description: 'Gouttes pour les yeux'
  },
  {
    id: 'antiglaucoma',
    names: ['antiglaucomateux', 'glaucome', 'timolol', 'timoptol', 'latanoprost', 'xalatan', 'travoprost', 'travatan', 'bimatoprost', 'lumigan', 'brinzolamide', 'azopt', 'dorzolamide', 'trusopt'],
    category: 'Ophtalmique et ORL',
    subcategory: 'Antiglaucomateux',
    description: 'Médicaments contre le glaucome'
  },
  {
    id: 'nasal_decongestant',
    names: ['décongestionnant nasal', 'décongestionnants nasaux', 'nez bouché', 'rhinite', 'pernazène', 'oxymétazoline', 'aturgyl', 'dérinox', 'déturgylone', 'humoxal', 'rhinadvil'],
    category: 'Ophtalmique et ORL',
    subcategory: 'Décongestionnants nasaux',
    description: 'Médicaments pour décongestionner le nez'
  },
  {
    id: 'orl_antiseptic',
    names: ['antiseptique orl', 'angine', 'mal de gorge', 'strepsils', 'lysopaïne', 'hexaspray', 'colludol', 'drill', 'humex gorge'],
    category: 'Ophtalmique et ORL',
    subcategory: 'Antiseptiques ORL',
    description: 'Antiseptiques pour la gorge et ORL'
  },

  // 12. MÉDICAMENTS UROLOGIQUES ET GYNÉCOLOGIQUES
  {
    id: 'urinary_antispasmodic',
    names: ['antispasmodique urinaire', 'vessie hyperactive', 'oxybutynine', 'ditropan', 'vésicare', 'solifénacine', 'toviaz', 'fésotérodine', 'trospium', 'ceris', 'mirabégron', 'betmiga'],
    category: 'Urologique et gynécologique',
    subcategory: 'Antispasmodiques urinaires',
    description: 'Médicaments pour les troubles urinaires'
  },
  {
    id: 'prostate_treatment',
    names: ['prostate', 'hypertrophie prostate', 'hbp', 'tamsulosine', 'omix', 'josir', 'alfuzosine', 'xatral', 'finastéride', 'chibro-proscar', 'dutastéride', 'avodart', 'combodart', 'silodosine', 'urorec'],
    category: 'Urologique et gynécologique',
    subcategory: 'Traitements prostate',
    description: 'Médicaments pour l\'hypertrophie de la prostate'
  },
  {
    id: 'ovulation_inducer',
    names: ['inducteur ovulation', 'stimulation ovulation', 'clomifène', 'clomid', 'gonadotrophine', 'gonal-f', 'puregon', 'ménopur', 'ovitrelle'],
    category: 'Urologique et gynécologique',
    subcategory: 'Inducteurs de l\'ovulation',
    description: 'Médicaments pour stimuler l\'ovulation'
  },
  {
    id: 'genital_infection_treatment',
    names: ['infection génitale', 'mycose vaginale', 'gynécologique', 'monazol', 'gyno-pevaryl', 'lomexin', 'flagyl ovule', 'polygynax'],
    category: 'Urologique et gynécologique',
    subcategory: 'Traitements infections génitales',
    description: 'Traitements des infections génitales'
  },
  {
    id: 'erectile_dysfunction',
    names: ['dysfonction érectile', 'impuissance', 'viagra', 'sildénafil', 'cialis', 'tadalafil', 'lévitra', 'vardénafil', 'spedra', 'avanafil'],
    category: 'Urologique et gynécologique',
    subcategory: 'Troubles érectiles',
    description: 'Médicaments pour la dysfonction érectile'
  },

  // 13. MÉDICAMENTS DE PRÉVENTION
  {
    id: 'vaccine',
    names: ['vaccin', 'vaccins', 'vaccination', 'immunisation', 'infanrix', 'revaxis', 'boostrix', 'prevenar', 'pneumovax', 'gardasil', 'fluarix', 'vaxigrip', 'influvac', 'havrix', 'engerix'],
    category: 'Prévention',
    subcategory: 'Vaccins',
    description: 'Vaccins préventifs'
  },
  {
    id: 'immunoglobulin',
    names: ['immunoglobuline', 'immunoglobulines', 'sérum', 'sérums immunologiques', 'tégéline', 'gammagard', 'octagam', 'privigen'],
    category: 'Prévention',
    subcategory: 'Sérums immunologiques',
    description: 'Immunoglobulines et sérums'
  },
  {
    id: 'prophylactic',
    names: ['prophylactique', 'prophylaxie', 'prévention', 'antipaludéen', 'malarone', 'lariam', 'méfloquine', 'doxypalu', 'prep', 'truvada préventif'],
    category: 'Prévention',
    subcategory: 'Traitements prophylactiques',
    description: 'Traitements préventifs'
  },

  // 14. AUTRES CATÉGORIES
  {
    id: 'emergency_medication',
    names: ['médicament urgence', 'adrénaline', 'anapen', 'epipen', 'jext', 'glucagon', 'glucagen', 'naloxone', 'narcan'],
    category: 'Autres',
    subcategory: 'Médicaments d\'urgence',
    description: 'Médicaments pour situations d\'urgence'
  },
  {
    id: 'diagnostic_medication',
    names: ['produit contraste', 'iode contraste', 'gadolinium', 'omnipaque', 'visipaque', 'dotarem', 'prohance'],
    category: 'Autres',
    subcategory: 'Médicaments de diagnostic',
    description: 'Produits de contraste et diagnostic'
  },
  {
    id: 'radiopharmaceutical',
    names: ['radiopharmaceutique', 'radioactif', 'iode radioactif', 'iode 131', 'technétium'],
    category: 'Autres',
    subcategory: 'Médicaments radiopharmaceutiques',
    description: 'Médicaments radioactifs'
  },
  {
    id: 'generic',
    names: ['générique', 'génériques'],
    category: 'Autres',
    subcategory: 'Génériques',
    description: 'Médicaments génériques'
  },
  {
    id: 'orphan_drug',
    names: ['médicament orphelin', 'maladie rare'],
    category: 'Autres',
    subcategory: 'Médicaments orphelins',
    description: 'Médicaments pour maladies rares'
  },

  // COMPLÉMENTS - Médicaments courants supplémentaires
  {
    id: 'iron_supplement',
    names: ['fer', 'supplément fer', 'tardyferon', 'ferograd', 'fumafer', 'timoferol', 'ferrostrane', 'carence fer'],
    category: 'Suppléments',
    subcategory: 'Fer',
    description: 'Suppléments en fer'
  },
  {
    id: 'calcium_vitamin_d',
    names: ['calcium', 'vitamine d', 'cacit', 'orocal', 'caltrate', 'fixical', 'uvedose', 'zymad', 'calcifix'],
    category: 'Suppléments',
    subcategory: 'Calcium/Vitamine D',
    description: 'Suppléments calciques et vitamine D'
  },
  {
    id: 'bisphosphonate',
    names: ['bisphosphonate', 'bisphosphonates', 'ostéoporose', 'alendronate', 'fosamax', 'fosavance', 'actonel', 'risédronate', 'bonviva', 'ibandronate', 'zometa', 'acide zolédronique'],
    category: 'Os et articulations',
    subcategory: 'Bisphosphonates',
    description: 'Médicaments contre l\'ostéoporose'
  },
  {
    id: 'muscle_relaxant',
    names: ['myorelaxant', 'myorelaxants', 'relaxant musculaire', 'décontractant', 'coltramyl', 'thiocolchicoside', 'méphénésine', 'décontractyl', 'baclofène', 'liorésal', 'tétrazépam'],
    category: 'Os et articulations',
    subcategory: 'Myorelaxants',
    description: 'Décontractants musculaires'
  },
  {
    id: 'antigout',
    names: ['goutte', 'antigoutte', 'allopurinol', 'zyloric', 'colchicine', 'colchimax', 'fébuxostat', 'adénuric'],
    category: 'Os et articulations',
    subcategory: 'Antigoutteux',
    description: 'Médicaments contre la goutte'
  }
];

// =====================================================
// TYPES D'ALLERGIES - LISTE EXHAUSTIVE
// =====================================================

export const allergyWarnings: AllergyWarning[] = [
  // 1. ALLERGIES RESPIRATOIRES
  {
    allergen: 'pollen',
    synonyms: ['rhume des foins', 'pollinose', 'allergie saisonnière', 'allergie graminées', 'allergie bouleau', 'allergie cyprès', 'allergie ambroisie', 'pollen bouleau', 'pollen graminées'],
    plantsToAvoid: ['Pollen en graine', 'Échinacée', 'Camomille', 'Pissenlit', 'Artichaut', 'Tournesol'],
    category: 'Allergies respiratoires',
    description: 'Allergies polliniques. Prudence avec le pollen en complément et les plantes de la même famille que le pollen allergisant (Astéracées notamment).'
  },
  {
    allergen: 'acariens',
    synonyms: ['acarien', 'allergie acariens', 'poussière', 'allergie poussière'],
    plantsToAvoid: [],
    category: 'Allergies respiratoires',
    description: 'Allergie aux acariens de la poussière. Pas de contre-indication spécifique aux plantes mais prudence avec les compléments contenant des excipients.'
  },
  {
    allergen: 'moisissures',
    synonyms: ['moisissure', 'allergie moisissures', 'champignons atmosphériques', 'alternaria', 'aspergillus'],
    plantsToAvoid: ['Champignons médicinaux', 'Reishi', 'Shiitake', 'Maitake', 'Cordyceps'],
    category: 'Allergies respiratoires',
    description: 'Allergie aux moisissures. Éviter les champignons médicinaux et les produits fermentés.'
  },
  {
    allergen: 'poils animaux',
    synonyms: ['poils animaux', 'squames', 'allergie chat', 'allergie chien', 'allergie animaux', 'poils chat', 'poils chien'],
    plantsToAvoid: [],
    category: 'Allergies respiratoires',
    description: 'Allergie aux poils et squames d\'animaux. Pas de contre-indication spécifique aux plantes.'
  },
  {
    allergen: 'blattes',
    synonyms: ['blatte', 'cafard', 'allergie blattes', 'allergie cafards'],
    plantsToAvoid: [],
    category: 'Allergies respiratoires',
    description: 'Allergie aux blattes/cafards. Pas de contre-indication spécifique aux plantes.'
  },

  // 2. ALLERGIES ALIMENTAIRES
  {
    allergen: 'arachide',
    synonyms: ['cacahuète', 'cacahuètes', 'allergie arachide', 'allergie cacahuète', 'peanut'],
    plantsToAvoid: ['Huile d\'arachide', 'Arachide', 'Beurre de cacahuète'],
    category: 'Allergies alimentaires',
    description: 'Allergie à l\'arachide. Risque d\'anaphylaxie. Éviter toute huile ou produit dérivé.'
  },
  {
    allergen: 'fruits à coque',
    synonyms: ['noix allergie', 'amande allergie', 'noisette allergie', 'noix', 'amande', 'noisette', 'pistache', 'noix de cajou', 'noix du brésil', 'noix de macadamia', 'noix de pécan', 'châtaigne', 'tree nuts'],
    plantsToAvoid: ['Huile de noix', 'Huile d\'amande', 'Huile d\'amande douce', 'Huile de noisette', 'Huile d\'argan', 'Noix', 'Amande', 'Noisette', 'Châtaigne', 'Huile de macadamia'],
    category: 'Allergies alimentaires',
    description: 'Allergie aux fruits à coque. Éviter les huiles végétales correspondantes et produits dérivés. Risque de réaction croisée.'
  },
  {
    allergen: 'lait',
    synonyms: ['lait de vache', 'allergie lait', 'allergie lactose', 'produits laitiers', 'lactose', 'caséine', 'protéines lait'],
    plantsToAvoid: ['Lactosérum', 'Probiotiques avec lactose', 'Compléments contenant du lactose'],
    category: 'Allergies alimentaires',
    description: 'Allergie aux protéines de lait de vache. Vérifier les excipients des compléments alimentaires.'
  },
  {
    allergen: 'oeuf',
    synonyms: ['œuf', 'oeufs', 'œufs', 'allergie oeuf', 'allergie œuf', 'albumine', 'ovalbumine'],
    plantsToAvoid: ['Lécithine d\'œuf', 'Compléments avec œuf'],
    category: 'Allergies alimentaires',
    description: 'Allergie à l\'œuf. Vérifier les excipients des compléments et cosmétiques (lécithine).'
  },
  {
    allergen: 'poisson',
    synonyms: ['allergie poisson', 'poissons', 'allergie poissons'],
    plantsToAvoid: ['Huile de poisson', 'Oméga-3 marins', 'Huile de foie de morue', 'Collagène marin'],
    category: 'Allergies alimentaires',
    description: 'Allergie au poisson. Éviter les huiles de poisson et oméga-3 d\'origine marine.'
  },
  {
    allergen: 'crustacés',
    synonyms: ['crevette', 'crabe', 'homard', 'langouste', 'langoustine', 'allergie crustacés', 'fruits de mer'],
    plantsToAvoid: ['Glucosamine marine', 'Chitosane', 'Chondroïtine marine', 'Astaxanthine de krill'],
    category: 'Allergies alimentaires',
    description: 'Allergie aux crustacés. Éviter les compléments d\'origine marine (glucosamine, chitosane).'
  },
  {
    allergen: 'mollusques',
    synonyms: ['moule', 'huître', 'coquillage', 'palourde', 'poulpe', 'seiche', 'calmar', 'allergie mollusques'],
    plantsToAvoid: ['Extrait de moule', 'Compléments marins'],
    category: 'Allergies alimentaires',
    description: 'Allergie aux mollusques. Éviter les compléments d\'origine marine.'
  },
  {
    allergen: 'soja',
    synonyms: ['soya', 'allergie soja', 'lécithine soja', 'protéine soja', 'isoflavones soja'],
    plantsToAvoid: ['Soja', 'Lécithine de soja', 'Isoflavones de soja', 'Protéines de soja', 'Huile de soja'],
    category: 'Allergies alimentaires',
    description: 'Allergie au soja. Éviter tous les dérivés du soja incluant la lécithine et les isoflavones.'
  },
  {
    allergen: 'gluten',
    synonyms: ['blé', 'maladie coeliaque', 'coeliaque', 'intolérance gluten', 'blé allergie', 'seigle', 'orge', 'avoine', 'épeautre'],
    plantsToAvoid: ['Herbe de blé', 'Orge', 'Malt d\'orge', 'Seigle', 'Avoine non certifiée', 'Épeautre', 'Germe de blé'],
    category: 'Allergies alimentaires',
    description: 'Maladie cœliaque ou sensibilité au gluten. Éviter les céréales contenant du gluten et leurs dérivés.'
  },
  {
    allergen: 'sésame',
    synonyms: ['allergie sésame', 'graines sésame', 'tahini', 'huile sésame'],
    plantsToAvoid: ['Sésame', 'Huile de sésame', 'Tahini'],
    category: 'Allergies alimentaires',
    description: 'Allergie au sésame. Éviter l\'huile de sésame et les graines.'
  },
  {
    allergen: 'céleri',
    synonyms: ['allergie céleri', 'céleri-rave', 'céleri-branche'],
    plantsToAvoid: ['Céleri', 'Livèche', 'Graines de céleri'],
    category: 'Allergies alimentaires',
    description: 'Allergie au céleri. Réaction croisée possible avec d\'autres Apiacées.'
  },
  {
    allergen: 'moutarde',
    synonyms: ['allergie moutarde', 'graines moutarde'],
    plantsToAvoid: ['Moutarde', 'Graines de moutarde', 'Cataplasme à la moutarde'],
    category: 'Allergies alimentaires',
    description: 'Allergie à la moutarde. Éviter les cataplasmes sinapisés.'
  },
  {
    allergen: 'lupin',
    synonyms: ['allergie lupin', 'farine lupin'],
    plantsToAvoid: ['Lupin', 'Farine de lupin'],
    category: 'Allergies alimentaires',
    description: 'Allergie au lupin. Réaction croisée possible avec arachide et autres légumineuses.'
  },

  // 3. ALLERGIES CUTANÉES
  {
    allergen: 'nickel',
    synonyms: ['nickel allergie', 'eczéma nickel', 'allergie nickel'],
    plantsToAvoid: ['Cacao en excès', 'Noix en excès', 'Soja', 'Avoine', 'Légumineuses en grande quantité'],
    category: 'Allergies cutanées',
    description: 'Allergie au nickel. Certains aliments riches en nickel peuvent déclencher des poussées d\'eczéma.'
  },
  {
    allergen: 'chrome',
    synonyms: ['allergie chrome', 'eczéma chrome'],
    plantsToAvoid: [],
    category: 'Allergies cutanées',
    description: 'Allergie au chrome. Prudence avec les compléments contenant du chrome.'
  },
  {
    allergen: 'latex',
    synonyms: ['latex allergie', 'hévéa', 'caoutchouc', 'gants latex'],
    plantsToAvoid: ['Pissenlit', 'Figuier', 'Papaye', 'Avocat', 'Banane', 'Kiwi', 'Châtaigne', 'Sarrasin'],
    category: 'Allergies cutanées',
    description: 'Syndrome latex-fruits. Réactions croisées avec certains fruits et plantes (avocat, banane, kiwi, châtaigne).'
  },
  {
    allergen: 'parfums',
    synonyms: ['allergie parfum', 'allergie parfums', 'fragrance', 'huiles essentielles allergie'],
    plantsToAvoid: ['Huiles essentielles parfumées', 'Lavande', 'Rose', 'Ylang-ylang', 'Baume du Pérou', 'Baume de Tolu'],
    category: 'Allergies cutanées',
    description: 'Allergie aux parfums et fragrances. Éviter les huiles essentielles aromatiques et baumes parfumés.'
  },
  {
    allergen: 'cosmétiques',
    synonyms: ['allergie cosmétique', 'allergie crème', 'eczéma cosmétique'],
    plantsToAvoid: [],
    category: 'Allergies cutanées',
    description: 'Allergie à certains composants cosmétiques. Tester avant toute application cutanée de produit naturel.'
  },
  {
    allergen: 'plantes contact',
    synonyms: ['eczéma plantes', 'lierre allergie', 'sumac allergie', 'primevère allergie', 'chrysanthème contact'],
    plantsToAvoid: ['Lierre', 'Primevère', 'Chrysanthème', 'Arnica en application cutanée', 'Herbe à puce'],
    category: 'Allergies cutanées',
    description: 'Dermatite de contact aux plantes. Éviter l\'application cutanée des plantes concernées.'
  },

  // 4. ALLERGIES MÉDICAMENTEUSES
  {
    allergen: 'pénicilline',
    synonyms: ['allergie pénicilline', 'pénicillines', 'amoxicilline allergie', 'bétalactamines'],
    plantsToAvoid: [],
    category: 'Allergies médicamenteuses',
    description: 'Allergie aux pénicillines. Pas de contre-indication spécifique aux plantes mais signaler au thérapeute.'
  },
  {
    allergen: 'aspirine',
    synonyms: ['allergie aspirine', 'acide acétylsalicylique', 'asthme aspirine', 'intolérance aspirine', 'triade widal'],
    plantsToAvoid: ['Saule', 'Reine-des-prés', 'Gaulthérie', 'Bouleau'],
    category: 'Allergies médicamenteuses',
    description: 'Intolérance/allergie à l\'aspirine. Éviter les plantes riches en salicylés.'
  },
  {
    allergen: 'ains',
    synonyms: ['allergie ains', 'anti-inflammatoire allergie', 'ibuprofène allergie'],
    plantsToAvoid: ['Saule', 'Reine-des-prés', 'Gaulthérie'],
    category: 'Allergies médicamenteuses',
    description: 'Allergie aux anti-inflammatoires non stéroïdiens. Prudence avec les plantes anti-inflammatoires.'
  },
  {
    allergen: 'sulfamides',
    synonyms: ['allergie sulfamide', 'sulfamides allergie', 'sulfonamide'],
    plantsToAvoid: [],
    category: 'Allergies médicamenteuses',
    description: 'Allergie aux sulfamides. Pas de contre-indication spécifique aux plantes.'
  },
  {
    allergen: 'anesthésiques',
    synonyms: ['allergie anesthésique', 'lidocaïne allergie', 'anesthésie locale'],
    plantsToAvoid: [],
    category: 'Allergies médicamenteuses',
    description: 'Allergie aux anesthésiques locaux. Signaler au thérapeute.'
  },
  {
    allergen: 'iode',
    synonyms: ['allergie iode', 'produit contraste', 'bétadine allergie', 'iode contraste'],
    plantsToAvoid: ['Algues iodées', 'Fucus', 'Laminaire', 'Kombu', 'Wakame', 'Nori', 'Varech'],
    category: 'Allergies médicamenteuses',
    description: 'Allergie/intolérance à l\'iode. Éviter les algues et produits riches en iode.'
  },

  // 5. ALLERGIES AUX PIQÛRES ET VENINS
  {
    allergen: 'abeille',
    synonyms: ['allergie abeille', 'piqûre abeille', 'venin abeille', 'apitoxine'],
    plantsToAvoid: ['Propolis', 'Miel', 'Gelée royale', 'Pollen d\'abeille', 'Cire d\'abeille', 'Apitoxine', 'Venin d\'abeille'],
    category: 'Allergies piqûres et venins',
    description: 'Allergie au venin d\'abeille. Toute l\'apithérapie est CONTRE-INDIQUÉE.'
  },
  {
    allergen: 'guêpe',
    synonyms: ['allergie guêpe', 'piqûre guêpe', 'venin guêpe'],
    plantsToAvoid: ['Propolis', 'Produits de la ruche'],
    category: 'Allergies piqûres et venins',
    description: 'Allergie au venin de guêpe. Prudence avec les produits de la ruche (réaction croisée possible).'
  },
  {
    allergen: 'frelon',
    synonyms: ['allergie frelon', 'piqûre frelon', 'venin frelon'],
    plantsToAvoid: ['Propolis', 'Produits de la ruche'],
    category: 'Allergies piqûres et venins',
    description: 'Allergie au venin de frelon. Prudence avec les produits de la ruche.'
  },
  {
    allergen: 'fourmi',
    synonyms: ['allergie fourmi', 'piqûre fourmi'],
    plantsToAvoid: [],
    category: 'Allergies piqûres et venins',
    description: 'Allergie aux piqûres de fourmis. Pas de contre-indication spécifique.'
  },
  {
    allergen: 'propolis',
    synonyms: ['propolis allergie', 'produits ruche allergie'],
    plantsToAvoid: ['Propolis', 'Miel', 'Gelée royale', 'Pollen', 'Cire d\'abeille'],
    category: 'Allergies piqûres et venins',
    description: 'Allergie aux produits de la ruche. Toute l\'apithérapie est contre-indiquée.'
  },

  // 6. ALLERGIES PROFESSIONNELLES
  {
    allergen: 'farine',
    synonyms: ['allergie farine', 'asthme boulanger', 'allergie blé professionnel'],
    plantsToAvoid: ['Blé', 'Seigle', 'Germe de blé'],
    category: 'Allergies professionnelles',
    description: 'Allergie à la farine (asthme du boulanger). Éviter les céréales en poudre.'
  },
  {
    allergen: 'latex professionnel',
    synonyms: ['latex soignant', 'gants latex professionnel'],
    plantsToAvoid: ['Pissenlit', 'Figuier', 'Papaye', 'Avocat', 'Banane', 'Kiwi', 'Châtaigne'],
    category: 'Allergies professionnelles',
    description: 'Allergie au latex en milieu professionnel. Syndrome latex-fruits.'
  },
  {
    allergen: 'produits chimiques',
    synonyms: ['allergie chimique', 'eczéma professionnel', 'dermatite professionnelle'],
    plantsToAvoid: [],
    category: 'Allergies professionnelles',
    description: 'Allergies professionnelles aux produits chimiques. Prudence avec les applications cutanées.'
  },
  {
    allergen: 'poussières industrielles',
    synonyms: ['poussière industrielle', 'asthme professionnel'],
    plantsToAvoid: [],
    category: 'Allergies professionnelles',
    description: 'Allergies aux poussières industrielles. Favoriser les formes liquides plutôt que poudres.'
  },

  // 7. ALLERGIES ENVIRONNEMENTALES DIVERSES
  {
    allergen: 'soleil',
    synonyms: ['photosensibilité', 'allergie soleil', 'lucite', 'urticaire solaire'],
    plantsToAvoid: ['Millepertuis', 'Angélique', 'Bergamote', 'Citron zeste', 'Orange amère', 'Céleri', 'Fenouil', 'Aneth', 'Rue', 'Khella'],
    category: 'Allergies environnementales',
    description: 'Photosensibilité. Éviter les plantes et HE photosensibilisantes (millepertuis, agrumes, Apiacées).'
  },
  {
    allergen: 'froid',
    synonyms: ['allergie froid', 'urticaire froid', 'urticaire au froid'],
    plantsToAvoid: [],
    category: 'Allergies environnementales',
    description: 'Urticaire au froid. Pas de contre-indication spécifique aux plantes.'
  },
  {
    allergen: 'eau',
    synonyms: ['allergie eau', 'urticaire aquagénique'],
    plantsToAvoid: [],
    category: 'Allergies environnementales',
    description: 'Urticaire aquagénique (très rare). Privilégier les formes sèches (gélules).'
  },
  {
    allergen: 'pollution',
    synonyms: ['allergie pollution', 'sensibilité pollution'],
    plantsToAvoid: [],
    category: 'Allergies environnementales',
    description: 'Sensibilité à la pollution. Les plantes dépuratives peuvent être bénéfiques.'
  },

  // 8. ALLERGIES CROISÉES
  {
    allergen: 'bouleau-fruits',
    synonyms: ['syndrome bouleau', 'allergie bouleau pomme', 'syndrome pollen-aliment'],
    plantsToAvoid: ['Pomme crue', 'Poire', 'Cerise', 'Pêche', 'Abricot', 'Prune', 'Noisette', 'Amande', 'Carotte crue', 'Céleri cru'],
    category: 'Allergies croisées',
    description: 'Syndrome bouleau-fruits. Réaction croisée entre pollen de bouleau et certains fruits/légumes crus.'
  },
  {
    allergen: 'latex-fruits',
    synonyms: ['syndrome latex fruits', 'allergie croisée latex'],
    plantsToAvoid: ['Banane', 'Avocat', 'Kiwi', 'Châtaigne', 'Papaye', 'Mangue', 'Fruit de la passion'],
    category: 'Allergies croisées',
    description: 'Syndrome latex-fruits. Réaction croisée entre latex et certains fruits tropicaux.'
  },
  {
    allergen: 'graminées-céréales',
    synonyms: ['syndrome graminées', 'allergie croisée graminées'],
    plantsToAvoid: ['Blé', 'Orge', 'Seigle', 'Maïs', 'Riz en cas sévère'],
    category: 'Allergies croisées',
    description: 'Réaction croisée graminées-céréales. Rarement cliniquement significative.'
  },

  // 9. FAMILLES BOTANIQUES
  {
    allergen: 'astéracées',
    synonyms: ['composées', 'asteraceae', 'compositae', 'marguerite allergie', 'camomille allergie', 'tournesol allergie', 'pissenlit allergie', 'artichaut allergie'],
    plantsToAvoid: ['Camomille', 'Échinacée', 'Arnica', 'Pissenlit', 'Artichaut', 'Chardon-marie', 'Achillée', 'Souci', 'Chrysanthème', 'Tournesol', 'Chicorée', 'Endive', 'Laitue', 'Tanaisie', 'Armoise', 'Absinthe', 'Estragon', 'Stevia', 'Grande camomille', 'Petite camomille', 'Bleuet'],
    category: 'Familles botaniques',
    description: 'Famille botanique très allergisante. Risque de réactions croisées entre toutes les Astéracées.'
  },
  {
    allergen: 'apiacées',
    synonyms: ['ombellifères', 'apiaceae', 'umbelliferae', 'carotte allergie', 'céleri allergie', 'persil allergie', 'aneth allergie'],
    plantsToAvoid: ['Fenouil', 'Anis', 'Carvi', 'Coriandre', 'Angélique', 'Livèche', 'Céleri', 'Persil', 'Carotte', 'Cumin', 'Aneth', 'Cerfeuil', 'Khella'],
    category: 'Familles botaniques',
    description: 'Famille des Apiacées. Allergies croisées fréquentes avec céleri, carotte, pollen de bouleau. Photosensibilisation possible.'
  },
  {
    allergen: 'lamiacées',
    synonyms: ['labiées', 'lamiaceae', 'labiatae', 'menthe allergie', 'lavande allergie', 'thym allergie', 'romarin allergie'],
    plantsToAvoid: ['Menthe', 'Lavande', 'Romarin', 'Thym', 'Sauge', 'Mélisse', 'Basilic', 'Origan', 'Marjolaine', 'Sarriette', 'Hysope', 'Marrube'],
    category: 'Familles botaniques',
    description: 'Famille des Lamiacées. Allergies possibles aux huiles essentielles de cette famille.'
  },
  {
    allergen: 'lauracées',
    synonyms: ['lauraceae', 'laurier allergie', 'cannelle allergie'],
    plantsToAvoid: ['Laurier', 'Cannelle', 'Avocat'],
    category: 'Familles botaniques',
    description: 'Famille des Lauracées. Réactions allergiques possibles.'
  },
  {
    allergen: 'rutacées',
    synonyms: ['rutaceae', 'agrumes allergie', 'citrus allergie'],
    plantsToAvoid: ['Orange', 'Citron', 'Pamplemousse', 'Bergamote', 'Mandarine', 'Rue'],
    category: 'Familles botaniques',
    description: 'Famille des agrumes. Photosensibilisation possible avec les zestes.'
  },
  {
    allergen: 'salicylés',
    synonyms: ['aspirine allergie', 'salicylate', 'acide salicylique', 'aspirine intolérance'],
    plantsToAvoid: ['Saule', 'Reine-des-prés', 'Gaulthérie', 'Bouleau', 'Peuplier'],
    category: 'Composés chimiques',
    description: 'Intolérance aux salicylés. Éviter les plantes riches en dérivés salicylés naturels.'
  },
  {
    allergen: 'solanacées',
    synonyms: ['solanaceae', 'tomate allergie', 'poivron allergie', 'aubergine allergie'],
    plantsToAvoid: ['Tomate', 'Poivron', 'Aubergine', 'Pomme de terre', 'Goji', 'Ashwagandha'],
    category: 'Familles botaniques',
    description: 'Famille des Solanacées. Réactions possibles avec plusieurs plantes de cette famille.'
  },
  {
    allergen: 'fabacées',
    synonyms: ['légumineuses', 'fabaceae', 'haricot allergie', 'lentille allergie', 'pois allergie'],
    plantsToAvoid: ['Soja', 'Arachide', 'Lupin', 'Lentille', 'Pois', 'Haricot', 'Fenugrec', 'Réglisse', 'Trèfle rouge', 'Luzerne'],
    category: 'Familles botaniques',
    description: 'Famille des légumineuses (Fabacées). Réactions croisées possibles entre membres de la famille.'
  }
];

// =====================================================
// INTERACTIONS PLANTES-MÉDICAMENTS
// =====================================================

export const plantMedicationInteractions: PlantMedicationInteraction[] = [
  // Millepertuis - INTERACTIONS MAJEURES
  {
    plant: 'millepertuis',
    medicationTypes: ['contraceptive', 'anticoagulant', 'antidepressant', 'immunosuppressant', 'antiepileptic', 'statin', 'cardiac_glycoside', 'chemotherapy', 'targeted_therapy', 'antiviral', 'cancer_hormone_therapy'],
    severity: 'major',
    description: 'Le millepertuis accélère le métabolisme de nombreux médicaments et peut diminuer leur efficacité de façon très significative.',
    mechanism: 'Induction des cytochromes P450 (CYP3A4, CYP1A2, CYP2C9) et de la glycoprotéine P'
  },
  // Ginkgo biloba
  {
    plant: 'ginkgo',
    medicationTypes: ['anticoagulant', 'antiaggregant', 'antiinflammatory'],
    severity: 'major',
    description: 'Risque d\'hémorragie augmenté. Ne pas associer avec des anticoagulants ou antiagrégants.',
    mechanism: 'Inhibition de l\'agrégation plaquettaire'
  },
  // Réglisse
  {
    plant: 'réglisse',
    medicationTypes: ['antihypertensive', 'diuretic', 'cardiac_glycoside', 'systemic_corticosteroid', 'ace_inhibitor', 'angiotensin_receptor_blocker'],
    severity: 'major',
    description: 'La réglisse peut augmenter la pression artérielle et causer une hypokaliémie. Contre-indiquée avec les antihypertenseurs et diurétiques.',
    mechanism: 'Effet minéralocorticoïde (rétention sodium, perte potassium)'
  },
  // Ginseng
  {
    plant: 'ginseng',
    medicationTypes: ['anticoagulant', 'antidepressant', 'oral_antidiabetic', 'insulin', 'antihypertensive', 'psychostimulant'],
    severity: 'moderate',
    description: 'Peut modifier l\'effet des anticoagulants et interagir avec les antidépresseurs (syndrome sérotoninergique).',
    mechanism: 'Effet sur la glycémie, inhibition plaquettaire, effet sérotoninergique'
  },
  // Valériane
  {
    plant: 'valériane',
    medicationTypes: ['anxiolytic', 'hypnotic', 'antidepressant', 'opioid', 'antiepileptic'],
    severity: 'moderate',
    description: 'Effet sédatif cumulatif avec les benzodiazépines et autres sédatifs. Risque de somnolence excessive.',
    mechanism: 'Potentialisation de l\'effet GABAergique'
  },
  // Passiflore
  {
    plant: 'passiflore',
    medicationTypes: ['anxiolytic', 'hypnotic', 'opioid'],
    severity: 'moderate',
    description: 'Peut renforcer l\'effet des sédatifs et anxiolytiques.',
    mechanism: 'Effet GABAergique'
  },
  // Ail
  {
    plant: 'ail',
    medicationTypes: ['anticoagulant', 'antiaggregant', 'antihypertensive'],
    severity: 'moderate',
    description: 'L\'ail a des propriétés anticoagulantes. Risque de saignement avec les anticoagulants.',
    mechanism: 'Inhibition de l\'agrégation plaquettaire'
  },
  // Sauge
  {
    plant: 'sauge',
    medicationTypes: ['oral_antidiabetic', 'antiepileptic', 'anxiolytic', 'menopause_treatment'],
    severity: 'moderate',
    description: 'Peut modifier la glycémie et avoir des effets pro-convulsivants à haute dose.',
    mechanism: 'Effet hypoglycémiant, neurotoxicité des thuyone à haute dose'
  },
  // Echinacée
  {
    plant: 'échinacée',
    medicationTypes: ['immunosuppressant', 'systemic_corticosteroid', 'biotherapy'],
    severity: 'moderate',
    description: 'Stimule le système immunitaire, peut contrecarrer l\'effet des immunosuppresseurs.',
    mechanism: 'Stimulation immunitaire'
  },
  // Curcuma
  {
    plant: 'curcuma',
    medicationTypes: ['anticoagulant', 'antiaggregant', 'antiinflammatory', 'chemotherapy'],
    severity: 'moderate',
    description: 'Peut augmenter le risque de saignement en association avec les anticoagulants.',
    mechanism: 'Inhibition de l\'agrégation plaquettaire, modulation du métabolisme hépatique'
  },
  // Gingembre
  {
    plant: 'gingembre',
    medicationTypes: ['anticoagulant', 'antiaggregant', 'oral_antidiabetic'],
    severity: 'minor',
    description: 'À doses élevées, peut légèrement augmenter le risque de saignement.',
    mechanism: 'Léger effet antiagrégant plaquettaire'
  },
  // Menthe poivrée
  {
    plant: 'menthe',
    medicationTypes: ['ppi', 'antiepileptic', 'iron_supplement'],
    severity: 'minor',
    description: 'Peut réduire l\'absorption de certains médicaments. Espacer les prises de 2h.',
    mechanism: 'Effet sur le transit gastrique'
  },
  // Camomille
  {
    plant: 'camomille',
    medicationTypes: ['anticoagulant', 'anxiolytic'],
    severity: 'minor',
    description: 'Léger effet anticoagulant et sédatif. Prudence avec les traitements associés.',
    mechanism: 'Coumarines, effet sédatif léger'
  },
  // Chardon-marie
  {
    plant: 'chardon-marie',
    medicationTypes: ['oral_antidiabetic', 'statin', 'chemotherapy'],
    severity: 'minor',
    description: 'Peut modifier le métabolisme de certains médicaments métabolisés par le foie.',
    mechanism: 'Inhibition légère des CYP450'
  },
  // Huiles essentielles générales
  {
    plant: 'huile essentielle',
    medicationTypes: ['anticoagulant', 'antiepileptic', 'antiinflammatory', 'anxiolytic'],
    severity: 'moderate',
    description: 'Certaines huiles essentielles peuvent interagir avec les médicaments. Demander conseil.',
    mechanism: 'Selon l\'huile essentielle spécifique'
  },
  // Ortie
  {
    plant: 'ortie',
    medicationTypes: ['anticoagulant', 'oral_antidiabetic', 'diuretic', 'antihypertensive', 'iron_supplement'],
    severity: 'minor',
    description: 'Peut potentialiser les effets des diurétiques et modifier la glycémie.',
    mechanism: 'Effet diurétique et hypoglycémiant léger'
  },
  // Griffonia (5-HTP)
  {
    plant: 'griffonia',
    medicationTypes: ['antidepressant', 'anxiolytic', 'antimigraine'],
    severity: 'major',
    description: 'Risque de syndrome sérotoninergique avec les antidépresseurs et triptans.',
    mechanism: 'Précurseur de la sérotonine - addition des effets sérotoninergiques'
  },
  // Kava
  {
    plant: 'kava',
    medicationTypes: ['anxiolytic', 'hypnotic', 'antipsychotic', 'anti_parkinson'],
    severity: 'major',
    description: 'Effet sédatif majeur, hépatotoxicité potentielle. Interaction avec les psychotropes.',
    mechanism: 'Effet GABAergique puissant, métabolisme hépatique'
  },
  // Éphédra (Ma Huang)
  {
    plant: 'éphédra',
    medicationTypes: ['antihypertensive', 'beta_blocker', 'antidepressant', 'psychostimulant', 'cardiac_glycoside'],
    severity: 'major',
    description: 'Stimulant cardiovasculaire puissant. Hypertension, tachycardie, AVC possibles.',
    mechanism: 'Effet sympathomimétique'
  },
  // Algues iodées
  {
    plant: 'algue',
    medicationTypes: ['thyroid_hormone', 'antithyroid'],
    severity: 'major',
    description: 'Teneur élevée en iode. Peut perturber la fonction thyroïdienne.',
    mechanism: 'Apport massif d\'iode'
  },
  // Berbérine
  {
    plant: 'berbérine',
    medicationTypes: ['oral_antidiabetic', 'statin', 'anticoagulant', 'immunosuppressant'],
    severity: 'moderate',
    description: 'Peut renforcer l\'effet hypoglycémiant et modifier le métabolisme hépatique.',
    mechanism: 'Inhibition CYP3A4, effet hypoglycémiant'
  },
  // Ashwagandha
  {
    plant: 'ashwagandha',
    medicationTypes: ['thyroid_hormone', 'anxiolytic', 'hypnotic', 'immunosuppressant'],
    severity: 'moderate',
    description: 'Peut modifier la fonction thyroïdienne et avoir un effet sédatif.',
    mechanism: 'Stimulation thyroïdienne, effet adaptogène'
  },
  // Cannelle
  {
    plant: 'cannelle',
    medicationTypes: ['oral_antidiabetic', 'anticoagulant'],
    severity: 'minor',
    description: 'Peut modifier la glycémie. La cannelle de Chine contient de la coumarine.',
    mechanism: 'Effet hypoglycémiant léger, coumarines'
  },
  // Fenugrec
  {
    plant: 'fenugrec',
    medicationTypes: ['oral_antidiabetic', 'insulin', 'anticoagulant'],
    severity: 'moderate',
    description: 'Effet hypoglycémiant significatif. Surveillance de la glycémie nécessaire.',
    mechanism: 'Effet hypoglycémiant, effet anticoagulant léger'
  },
  // Aubépine
  {
    plant: 'aubépine',
    medicationTypes: ['cardiac_glycoside', 'beta_blocker', 'calcium_channel_blocker', 'antiarrhythmic'],
    severity: 'moderate',
    description: 'Peut potentialiser les effets des médicaments cardiaques.',
    mechanism: 'Effet inotrope positif, chronotrope négatif'
  }
];

// =====================================================
// FONCTIONS UTILITAIRES
// =====================================================

// Fonction pour identifier le type de médicament
export function identifyMedicationType(medicationName: string): MedicationType | null {
  const nameLower = medicationName.toLowerCase().trim();
  
  for (const medType of medicationTypes) {
    if (medType.names.some(name => nameLower.includes(name.toLowerCase()))) {
      return medType;
    }
  }
  
  return null;
}

// Fonction pour identifier tous les types de médicaments correspondants
export function identifyAllMedicationTypes(medicationName: string): MedicationType[] {
  const nameLower = medicationName.toLowerCase().trim();
  const results: MedicationType[] = [];
  
  for (const medType of medicationTypes) {
    if (medType.names.some(name => nameLower.includes(name.toLowerCase()))) {
      results.push(medType);
    }
  }
  
  return results;
}

// Fonction pour obtenir toutes les interactions pour un médicament
export function getInteractionsForMedication(medicationName: string): PlantMedicationInteraction[] {
  const medType = identifyMedicationType(medicationName);
  
  if (!medType) return [];
  
  return plantMedicationInteractions.filter(interaction => 
    interaction.medicationTypes.includes(medType.id)
  );
}

// Fonction pour vérifier si une plante interagit avec un médicament
export function checkPlantMedicationInteraction(
  plantName: string, 
  medications: string[]
): { hasInteraction: boolean; interactions: { medication: string; interaction: PlantMedicationInteraction }[] } {
  const plantLower = plantName.toLowerCase();
  const interactions: { medication: string; interaction: PlantMedicationInteraction }[] = [];
  
  for (const medication of medications) {
    const medType = identifyMedicationType(medication);
    if (!medType) continue;
    
    for (const interaction of plantMedicationInteractions) {
      if (plantLower.includes(interaction.plant.toLowerCase()) && 
          interaction.medicationTypes.includes(medType.id)) {
        interactions.push({ medication, interaction });
      }
    }
  }
  
  return { hasInteraction: interactions.length > 0, interactions };
}

// Fonction pour obtenir les avertissements liés aux allergies
export function getAllergyWarningsForPatient(patientAllergies: string[]): AllergyWarning[] {
  const warnings: AllergyWarning[] = [];
  
  for (const allergy of patientAllergies) {
    const allergyLower = allergy.toLowerCase().trim();
    
    for (const warning of allergyWarnings) {
      if (allergyLower.includes(warning.allergen.toLowerCase()) ||
          warning.synonyms.some(syn => allergyLower.includes(syn.toLowerCase()))) {
        if (!warnings.find(w => w.allergen === warning.allergen)) {
          warnings.push(warning);
        }
      }
    }
  }
  
  return warnings;
}

// Fonction pour vérifier si une plante est contre-indiquée pour les allergies du patient
export function isPlantContraindicatedForAllergies(plantName: string, patientAllergies: string[]): { 
  contraindicated: boolean; 
  reason: string | null 
} {
  const plantLower = plantName.toLowerCase();
  const patientAllergyWarnings = getAllergyWarningsForPatient(patientAllergies);
  
  for (const warning of patientAllergyWarnings) {
    for (const plantToAvoid of warning.plantsToAvoid) {
      if (plantLower.includes(plantToAvoid.toLowerCase())) {
        return {
          contraindicated: true,
          reason: `⚠️ Contre-indiqué en raison de votre allergie (${warning.allergen}). ${warning.description}`
        };
      }
    }
  }
  
  return { contraindicated: false, reason: null };
}

// Fonction pour générer un résumé des interactions et allergies
export function generateMedicationAllergySummary(
  medications: string[], 
  allergies: string[]
): { medicationTypes: { category: string; subcategory?: string }[]; allergyWarnings: AllergyWarning[]; generalWarnings: string[] } {
  const identifiedMedTypes = medications
    .map(med => identifyMedicationType(med))
    .filter((t): t is MedicationType => t !== null);
  
  const allergyWarningsList = getAllergyWarningsForPatient(allergies);
  
  const generalWarnings: string[] = [];
  
  // Avertissements généraux selon les médicaments
  if (identifiedMedTypes.some(t => t.id === 'anticoagulant' || t.id === 'antiaggregant')) {
    generalWarnings.push('⚠️ Traitement anticoagulant/antiagrégant : éviter les plantes à effet fluidifiant (ail, ginkgo, curcuma, gingembre à haute dose).');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'antidepressant')) {
    generalWarnings.push('⚠️ Traitement antidépresseur : éviter le millepertuis et les plantes sérotoninergiques (griffonia, safran, 5-HTP).');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'anxiolytic' || t.id === 'hypnotic')) {
    generalWarnings.push('⚠️ Traitement sédatif : prudence avec les plantes calmantes (valériane, passiflore, houblon) - effet cumulatif possible.');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'immunosuppressant' || t.id === 'biotherapy')) {
    generalWarnings.push('⚠️ Traitement immunosuppresseur/biothérapie : éviter les plantes immunostimulantes (échinacée, astragale, champignons médicinaux).');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'oral_antidiabetic' || t.id === 'insulin' || t.id === 'glp1_agonist')) {
    generalWarnings.push('⚠️ Traitement antidiabétique : surveiller la glycémie avec les plantes hypoglycémiantes (cannelle, fenugrec, berbérine, gymnéma).');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'thyroid_hormone' || t.id === 'antithyroid')) {
    generalWarnings.push('⚠️ Traitement thyroïdien : éviter les algues iodées, espacer la prise de soja/café de 2h. Prudence avec l\'ashwagandha.');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'antihypertensive' || t.id === 'beta_blocker' || t.id === 'ace_inhibitor')) {
    generalWarnings.push('⚠️ Traitement antihypertenseur : éviter la réglisse (élévation tension), prudence avec le ginseng.');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'antiepileptic')) {
    generalWarnings.push('⚠️ Traitement antiépileptique : éviter les huiles essentielles neurotoxiques (sauge, thuya, absinthe). Prudence avec le millepertuis.');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'opioid')) {
    generalWarnings.push('⚠️ Traitement opioïde : éviter les plantes sédatives (valériane, kava, pavot) - risque de dépression respiratoire.');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'contraceptive' || t.id === 'menopause_treatment')) {
    generalWarnings.push('⚠️ Traitement hormonal : le millepertuis peut diminuer l\'efficacité contraceptive. Prudence avec les phytoestrogènes (soja, trèfle rouge).');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'chemotherapy' || t.id === 'targeted_therapy' || t.id === 'cancer_immunotherapy')) {
    generalWarnings.push('⚠️ Chimiothérapie/thérapie ciblée : éviter le millepertuis, le pamplemousse et les antioxydants à haute dose sans avis médical.');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'lithium')) {
    generalWarnings.push('⚠️ Traitement au lithium : éviter les plantes diurétiques (orthosiphon, piloselle, bouleau) - risque de surdosage.');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'cardiac_glycoside')) {
    generalWarnings.push('⚠️ Digitaliques : éviter la réglisse, le millepertuis, les laxatifs stimulants (séné, bourdaine). Risque d\'arythmie.');
  }
  
  if (identifiedMedTypes.some(t => t.id === 'ppi' || t.id === 'h2_blocker' || t.id === 'antacid')) {
    generalWarnings.push('⚠️ Traitement antiacide/IPP : espacer la prise des plantes et suppléments de 2h pour une bonne absorption.');
  }
  
  // Avertissements pour allergies spécifiques
  if (allergyWarningsList.some(w => w.allergen === 'propolis' || w.allergen === 'abeille')) {
    generalWarnings.push('🚫 ALLERGIE PRODUITS DE LA RUCHE : Toute l\'apithérapie est CONTRE-INDIQUÉE (miel, propolis, gelée royale, pollen).');
  }
  
  if (allergyWarningsList.some(w => w.allergen === 'astéracées')) {
    generalWarnings.push('🚫 ALLERGIE ASTÉRACÉES : Éviter camomille, échinacée, arnica, pissenlit, artichaut, chardon-marie et toutes les Composées.');
  }
  
  if (allergyWarningsList.some(w => w.allergen === 'latex' || w.allergen === 'latex-fruits')) {
    generalWarnings.push('🚫 SYNDROME LATEX-FRUITS : Éviter banane, avocat, kiwi, châtaigne, papaye et certaines plantes à latex (pissenlit, figuier).');
  }
  
  return {
    medicationTypes: identifiedMedTypes.map(t => ({ category: t.category, subcategory: t.subcategory })),
    allergyWarnings: allergyWarningsList,
    generalWarnings
  };
}

// Liste des médicaments courants pour suggestions
export const commonMedicationSuggestions = [
  // Douleur et fièvre
  'Paracétamol', 'Anti-inflammatoire (AINS)', 'Aspirine', 'Opioïde/Tramadol',
  // Cardiovasculaire
  'Anticoagulant', 'Antiagrégant', 'Bêta-bloquant', 'Antihypertenseur', 'Statine', 'Diurétique', 'IEC/Sartan',
  // Diabète
  'Antidiabétique oral', 'Insuline', 'GLP-1 (Ozempic/Trulicity)',
  // Thyroïde
  'Lévothyrox', 'Antithyroïdien',
  // Système nerveux
  'Antidépresseur', 'Anxiolytique', 'Somnifère', 'Antiépileptique', 'Neuroleptique', 'Lithium',
  // Hormones
  'Pilule contraceptive', 'Traitement ménopause', 'Corticoïdes',
  // Immunité
  'Immunosuppresseur', 'Biothérapie',
  // Digestif
  'IPP (anti-acide)', 'Antispasmodique',
  // Respiratoire
  'Bronchodilatateur', 'Corticoïde inhalé',
  // Cancer
  'Chimiothérapie', 'Hormonothérapie cancer',
  // Autres
  'Antibiotique', 'Antifongique', 'Antihistaminique'
];

// Liste des allergies courantes pour suggestions
export const commonAllergySuggestions = [
  // Respiratoires
  'Pollen', 'Acariens', 'Moisissures', 'Poils animaux',
  // Alimentaires
  'Arachide', 'Fruits à coque', 'Lait', 'Œuf', 'Poisson', 'Crustacés', 'Soja', 'Gluten/Blé', 'Sésame',
  // Cutanées/Contact
  'Latex', 'Nickel', 'Parfums',
  // Médicamenteuses
  'Pénicilline', 'Aspirine/AINS', 'Iode',
  // Venins
  'Abeille/Propolis', 'Guêpe',
  // Familles botaniques
  'Astéracées (Camomille)', 'Apiacées (Céleri)', 'Lamiacées (Menthe/Lavande)',
  // Croisées
  'Bouleau-fruits', 'Latex-fruits',
  // Autres
  'Salicylés', 'Photosensibilité (soleil)'
];
